import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faLock, 
  faUser, 
  faBriefcase, 
  faBuilding, 
  faMapMarkerAlt,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { useFormik, FormikProps } from 'formik';
import * as Yup from 'yup';
import MainLayout from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import api from '../services/api'; // Import the api service instance
import { geocodeAndOffsetLocation } from '../utils/geocoding'; // Import from new util file

// Work type options
const workTypeOptions = [
  { value: '', label: 'Select work type...' },
  { value: 'on-site', label: 'On-site' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' }
];

// Experience level options
const experienceLevelOptions = [
  { value: '', label: 'Select experience level...' },
  { value: 'junior', label: 'Junior (0-3 years)' },
  { value: 'mid-level', label: 'Mid-Level (4-7 years)' },
  { value: 'senior', label: 'Senior (8+ years)' },
  { value: 'lead', label: 'Lead/Principal (10+ years)' },
  { value: 'manager', label: 'Manager (8+ years)' },
  { value: 'executive', label: 'Executive (12+ years)' }
];

// Profession options
const professionOptions = [
  { value: '', label: 'Select your profession...' },
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'legal', label: 'Legal' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'human_resources', label: 'Human Resources' },
  { value: 'arts_entertainment', label: 'Arts & Entertainment' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail' },
  { value: 'government', label: 'Government' },
  { value: 'energy', label: 'Energy' },
  { value: 'student', label: 'Student' },
  { value: 'retired', label: 'Retired' },
  { value: 'other', label: 'Other' },
];

interface SkillItemProps {
  skill: string;
  onRemove: (skill: string) => void;
}

const SkillItem: React.FC<SkillItemProps> = ({ skill, onRemove }) => {
  return (
    <div className="bg-light rounded-pill px-3 py-1 d-inline-flex align-items-center me-2 mb-2">
      <span>{skill}</span>
      <Button 
        variant="link" 
        className="p-0 ms-2 text-secondary" 
        onClick={() => onRemove(skill)}
        aria-label={`Remove skill ${skill}`}
      >
        <i className="fas fa-times"></i>
      </Button>
    </div>
  );
};

// Define the shape of Formik values
interface FormValues {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  profession: string;
  experience: string;
  company: string;
  workType: string;
  linkedin: string;
  country: string;
  city: string;
  zipcode: string;
}

// Props for Step Components
interface StepProps {
  formik: FormikProps<FormValues>;
}

interface Step1Props extends StepProps {
  showPassword: boolean;
  togglePasswordVisibility: () => void;
}

interface Step2Props extends StepProps {
  skills: string[];
  skillInput: string;
  setSkillInput: React.Dispatch<React.SetStateAction<string>>;
  addSkill: () => void;
  handleSkillInputKeyDown: (e: React.KeyboardEvent) => void;
  removeSkill: (skillToRemove: string) => void;
}

const Step1AccountInfo: React.FC<Step1Props> = ({ formik, showPassword, togglePasswordVisibility }) => (
                    <>
                      <h4 className="mb-3">Account Information</h4>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <FontAwesomeIcon icon={faEnvelope} className="text-muted" />
                          </span>
                          <Form.Control
                            type="email"
                            placeholder="Enter your email"
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.email && !!formik.errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.email}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Password</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <FontAwesomeIcon icon={faLock} className="text-muted" />
                          </span>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Create a password"
                            name="password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.password && !!formik.errors.password}
                          />
                          <Button 
                            variant="light" 
                            onClick={togglePasswordVisibility}
                            className="border"
          aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.password}
                          </Form.Control.Feedback>
                        </div>
                        <Form.Text className="text-muted">
                          Password must be at least 8 characters with a number and uppercase letter.
                        </Form.Text>
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label>Confirm Password</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <FontAwesomeIcon icon={faLock} className="text-muted" />
                          </span>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            name="confirmPassword"
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.confirmPassword}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </>
);

const Step2ProfessionalInfo: React.FC<Step2Props> = ({
  formik,
  skills,
  skillInput,
  setSkillInput,
  addSkill,
  handleSkillInputKeyDown,
  removeSkill,
}) => (
                    <>
                      <h4 className="mb-3">Professional Information</h4>
                      
                      <Form.Group className="mb-3">
      <Form.Label>Full Name (Optional)</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <FontAwesomeIcon icon={faUser} className="text-muted" />
                          </span>
                          <Form.Control
                            type="text"
                            placeholder="Enter your full name"
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.name && !!formik.errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.name}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Profession</Form.Label>
      <Form.Select
                            name="profession"
                            value={formik.values.profession}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.profession && !!formik.errors.profession}
      >
        {professionOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.profession}
                          </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Experience Level</Form.Label>
                        <Form.Select
                          name="experience"
                          value={formik.values.experience}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.experience && !!formik.errors.experience}
                        >
                          {experienceLevelOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.experience}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Company/Organization</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <FontAwesomeIcon icon={faBuilding} className="text-muted" />
                          </span>
                          <Form.Control
                            type="text"
                            placeholder="Where do you work? (Optional)"
                            name="company"
                            value={formik.values.company}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.company && !!formik.errors.company}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.company}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Work Type</Form.Label>
                        <Form.Select
                          name="workType"
                          value={formik.values.workType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.workType && !!formik.errors.workType}
                        >
                          {workTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.workType}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>LinkedIn Profile (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="https://www.linkedin.com/in/your-profile"
                          name="linkedin"
                          value={formik.values.linkedin}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.linkedin && !!formik.errors.linkedin}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.linkedin}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label>Skills</Form.Label>
                        <div className="input-group mb-2">
                          <Form.Control
                            type="text"
                            placeholder="Add your professional skills"
                            value={skillInput}
                            onChange={(e) => setSkillInput(e.target.value)}
                            onKeyDown={handleSkillInputKeyDown}
                          />
                          <Button 
                            variant="primary" 
                            onClick={addSkill}
                            disabled={!skillInput.trim()}
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {skills.length === 0 ? (
                            <span className="text-muted">No skills added yet</span>
                          ) : (
                            skills.map((skill, index) => (
                              <SkillItem 
                                key={index} 
                                skill={skill} 
                                onRemove={removeSkill} 
                              />
                            ))
                          )}
                        </div>
                      </Form.Group>
                    </>
);
                  
const Step3Location: React.FC<StepProps> = ({ formik }) => (
                    <>
                      <h4 className="mb-3">Your Location</h4>
                      <p className="text-muted mb-4">
                        We use approximate locations to protect your privacy. 
                        Your exact address is never shared with other users.
                      </p>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter your country"
                          name="country"
                          value={formik.values.country}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.country && !!formik.errors.country}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.country}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter your city"
                          name="city"
                          value={formik.values.city}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.city && !!formik.errors.city}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.city}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label>
                          {formik.values.country?.toLowerCase() === 'united states' || 
                           formik.values.country?.toLowerCase() === 'us' 
                            ? 'ZIP Code (Required for US locations)'
                            : 'Postal/ZIP Code (Optional)'
                          }
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter your postal/ZIP code"
                          name="zipcode"
                          value={formik.values.zipcode}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.zipcode && !!formik.errors.zipcode}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.zipcode}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <div className="mb-4">
                        <Alert variant="info" className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="text-info me-3 fs-4" />
                          <div>
                            After registration, you'll be able to see other data professionals 
                            in your area and they'll be able to see your approximate location.
                          </div>
                        </Alert>
                      </div>
                    </>
);

// Updated: Real async email check function using backend API
const checkEmailAvailability = async (email: string): Promise<boolean> => {
  if (!email) {
    return true; // Don't call API for empty email
  }
  try {
    const response = await api.post('/auth/check-email', { email });
    // Assuming backend responds with { isAvailable: boolean }
    return response.data.isAvailable;
  } catch (error) {
    console.error('API check email error:', error);
    // In case of error, assume email is *not* available to prevent potential issues
    // Alternatively, could return true and let backend handle final registration check
    return false; 
  }
};

const RegisterPage: React.FC = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  
  // Validation schema
  const validationSchema = [
    // Step 1: Account Information
    Yup.object({
      email: Yup.string()
        .trim()
        .email('Invalid email address')
        .required('Email is required')
        // Yup test now uses the real API check function
        .test('email-available', 'This email address is already taken.', async (value) => {
          if (!value) return true; 
          try {
            const isAvailable = await checkEmailAvailability(value);
            return isAvailable;
          } catch (error) {
            // Error is logged within checkEmailAvailability
            return false; // Treat check failure as email unavailable
          }
        }),
      password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Please confirm your password')
    }),
    // Step 2: Professional Information
    Yup.object({
      name: Yup.string()
        .trim(),
      profession: Yup.string()
        .required('Profession is required'),
      experience: Yup.string()
        .required('Experience level is required'),
      company: Yup.string().trim(),
      workType: Yup.string()
        .required('Work type is required'),
      linkedin: Yup.string()
        .trim()
        .url('Please enter a valid LinkedIn URL or leave it blank')
    }),
    // Step 3: Location
    Yup.object({
      country: Yup.string()
        .trim()
        .required('Country is required'),
      city: Yup.string()
        .trim()
        .required('City is required'),
      zipcode: Yup.string()
        .trim()
        .when('country', {
          is: (country: string) => !!country && (country.toLowerCase() === 'united states' || country.toLowerCase() === 'us'),
          then: (schema) => schema.required('ZIP code is required for US locations')
        })
    })
  ];
  
  // Form handling
  const formik = useFormik<FormValues>({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      profession: '',
      experience: '',
      company: '',
      workType: '',
      linkedin: '',
      country: 'United States', // Default country
      city: '',
      zipcode: '',
    },
    validationSchema: validationSchema[currentStep - 1],
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      // --- Handle "Next" button --- 
      if (currentStep < totalSteps) {
        // console.log(`[Debug] Clicked Next on step ${currentStep}. Validating...`); // Removed log
        const errors = await formik.validateForm(); 
        // console.log('[Debug] Validation completed. Errors:', JSON.stringify(errors)); // Removed log
        
        const currentSchemaFields = Object.keys(validationSchema[currentStep - 1].fields);
        const currentStepErrors = Object.keys(errors).filter(key => 
          currentSchemaFields.includes(key)
        );
        // console.log('[Debug] Current Step Errors Found:', JSON.stringify(currentStepErrors)); // Removed log

        if (currentStepErrors.length === 0) {
          // console.log('[Debug] No errors found for step, proceeding.'); // Removed log
          setCurrentStep(currentStep + 1);
          setRegisterError('');
          setGeocodingError(null);
        } else {
          // console.log('[Debug] Errors found for step, touching fields.'); // Removed log
          formik.setTouched(
            currentSchemaFields.reduce((acc, key) => ({ ...acc, [key]: true }), {})
          );
        }
        return; 
      }

      // --- Final Step Submission (currentStep === totalSteps) --- 
      // Ensure final step is also valid before submitting (including geocoding later)
      const finalErrors = await formik.validateForm();
      if (Object.keys(finalErrors).length > 0) {
         console.log('Validation errors on final submit attempt', finalErrors);
         formik.setTouched(
            Object.keys(validationSchema[totalSteps - 1].fields).reduce((acc, key) => ({ ...acc, [key]: true }), {})
          );
         return; // Don't proceed to geocoding/API call if basic validation fails
      }

      // Start loading indicator for geocoding/API call
      setIsLoading(true);
      setRegisterError('');
      setGeocodingError(null);
      let finalLatitude: number | null = null;
      let finalLongitude: number | null = null;

      // 1. Geocode Location (Now using imported function)
      try {
        const coords = await geocodeAndOffsetLocation(
          values.city,
          values.country,
          values.zipcode
        );
        if (coords) {
          finalLatitude = coords.latitude;
          finalLongitude = coords.longitude;
        } else {
          // Handle geocoding failure
          setGeocodingError("Could not verify location...");
          setIsLoading(false);
          return;
        }
      } catch (error) {
        // Handle geocoding error
        setGeocodingError("An error occurred while verifying location.");
        setIsLoading(false);
        return;
      }
      
      // 2. Prepare User Data (including geocoded coords)
      const userData = {
        email: values.email,
        password: values.password,
        name: values.name,
        profession: values.profession,
        experience: values.experience,
        company: values.company,
        work_type: values.workType,
        linkedin: values.linkedin,
        location: {
          country: values.country,
          city: values.city,
          zipcode: values.zipcode,
          latitude: finalLatitude,
          longitude: finalLongitude,
        },
        skills: skills
      };

      // 3. Call Registration API
      try {
        const result = await register(userData); 
        if (result.success) {
          navigate('/map');
        } else {
          const errorMessage = result.message || 'Registration failed';
          setRegisterError(errorMessage);
          const lowerCaseError = errorMessage.toLowerCase();
          if (lowerCaseError.includes('email') || lowerCaseError.includes('password')) {
              setCurrentStep(1);
          } else if (
              lowerCaseError.includes('name') ||
              lowerCaseError.includes('profession') ||
              lowerCaseError.includes('experience') ||
              lowerCaseError.includes('company') ||
              lowerCaseError.includes('work type') ||
              lowerCaseError.includes('linkedin') ||
              lowerCaseError.includes('skill')
          ) {
              setCurrentStep(2);
          } else if (
              lowerCaseError.includes('country') ||
              lowerCaseError.includes('city') ||
              lowerCaseError.includes('zip') ||
              lowerCaseError.includes('location')
          ) {
              setCurrentStep(3);
          }
        }
      } catch (error) {
        console.error('Registration error:', error);
        setRegisterError('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  });
  
  // Function to handle going back to previous step
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Password visibility toggling
  const [showPassword, setShowPassword] = useState(false);
  
  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };
  
  // Skills management
  const addSkill = () => {
    const trimmedSkill = skillInput.trim();
    if (trimmedSkill) {
      if (!skills.includes(trimmedSkill)) {
        setSkills([...skills, trimmedSkill]);
        setSkillInput('');
      } else {
        console.warn(`[Debug] Skill "${trimmedSkill}" already added.`); 
        setSkillInput('');
      }
    }
  };
  const handleSkillInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addSkill();
    }
  };
  const removeSkill = (skillToRemove: string) => {
    setSkills(skills.filter(skill => skill !== skillToRemove));
  };
  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1AccountInfo formik={formik} showPassword={showPassword} togglePasswordVisibility={togglePasswordVisibility} />;
      case 2:
        return <Step2ProfessionalInfo formik={formik} skills={skills} skillInput={skillInput} setSkillInput={setSkillInput} addSkill={addSkill} handleSkillInputKeyDown={handleSkillInputKeyDown} removeSkill={removeSkill} />;
      case 3:
        return <Step3Location formik={formik} />;
      default:
        return null;
    }
  };

  return (
    <MainLayout>
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={8} md={10}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-lg-5 p-4">
                <div className="text-center mb-4">
                  <h2 className="fw-bold">Create Your Account</h2>
                  <p className="text-muted">Join the DataNest professional network</p>
                </div>

                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Step {currentStep} of {totalSteps}</span>
                    <span>{Math.round(progress)}% Complete</span>
                  </div>
                  <div className="progress">
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                </div>

                {geocodingError && (
                  <Alert variant="warning" className="mb-4">
                    {geocodingError}
                  </Alert>
                )}
                {registerError && !geocodingError && (
                  <Alert variant="danger" className="mb-4">
                    {registerError}
                  </Alert>
                )}

                <Form onSubmit={formik.handleSubmit}>
                  {renderStepContent()}

                  <div className="d-flex justify-content-between mt-5">
                    {currentStep > 1 ? (
                      <Button 
                        variant="outline-secondary" 
                        onClick={handleBack}
                        disabled={isLoading}
                      >
                        Back
                      </Button>
                    ) : (
                      <div></div>
                    )}
                    <Button 
                      variant="primary" 
                      type="submit" 
                      disabled={isLoading || !formik.isValid && formik.submitCount > 0}
                    >
                      {isLoading ? (
                        <LoadingSpinner size="sm" text={currentStep === totalSteps ? "Creating account..." : "Next..."} className="text-white" />
                      ) : (
                        currentStep === totalSteps ? (
                          <>
                            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                            Create Account
                          </>
                        ) : (
                          'Next'
                        )
                      )}
                    </Button>
                  </div>
                  
                  {currentStep === 1 && (
                    <div className="text-center mt-4">
                      <p className="mb-0">
                        Already have an account? <Link to="/login" className="text-primary">Sign in</Link>
                      </p>
                    </div>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </MainLayout>
  );
};

export default RegisterPage;